import { useMemo } from 'react';
import { scaleLinear } from 'd3';
import { Chart } from './Chart';

const SECTOR_COLORS = ['#e10600', '#f7f00c', '#3b82f6'];

function buildOwnership(laps) {
  // For each (lap_number, sector), find the driver with the lowest sector time.
  const byLap = new Map();
  let lapMin = Infinity, lapMax = -Infinity;
  for (const l of laps) {
    if (l.is_pit_in || l.is_pit_out) continue;
    const lap = Number(l.lap_number);
    if (!isFinite(lap)) continue;
    const s = [l.sector1_seconds, l.sector2_seconds, l.sector3_seconds].map((v) =>
      v == null ? null : Number(v),
    );
    if (s.some((v) => v == null || !isFinite(v) || v <= 0)) continue;
    if (lap < lapMin) lapMin = lap;
    if (lap > lapMax) lapMax = lap;
    if (!byLap.has(lap)) byLap.set(lap, []);
    byLap.get(lap).push({ driver: l.driver, s });
  }

  const ownership = new Map(); // driver -> {0:[laps], 1:[laps], 2:[laps]}
  for (const [lap, entries] of byLap.entries()) {
    for (let k = 0; k < 3; k++) {
      let best = null;
      for (const e of entries) {
        if (best == null || e.s[k] < best.s[k]) best = e;
      }
      if (!best) continue;
      if (!ownership.has(best.driver)) ownership.set(best.driver, [[], [], []]);
      ownership.get(best.driver)[k].push(lap);
    }
  }

  const rows = [];
  for (const [driver, sectors] of ownership.entries()) {
    const total = sectors[0].length + sectors[1].length + sectors[2].length;
    rows.push({ driver, sectors, total });
  }
  rows.sort((a, b) => b.total - a.total);
  return { rows, lapMin: isFinite(lapMin) ? lapMin : 1, lapMax: isFinite(lapMax) ? lapMax : 1 };
}

export const SectorDominance = ({ laps }) => {
  const { rows, lapMin, lapMax } = useMemo(() => buildOwnership(laps || []), [laps]);

  if (rows.length === 0) {
    return (
      <div className="absolute inset-0 grid place-items-center text-xs text-f1-muted px-6 text-center">
        No sector times available — backfill the worker for sector timing.
      </div>
    );
  }

  return (
    <Chart>
      {({ width, height }) => {
        const margin = { top: 4, right: 36, bottom: 16, left: 36 };
        const innerW = Math.max(0, width - margin.left - margin.right);
        const innerH = Math.max(0, height - margin.top - margin.bottom);

        const visible = rows.slice(0, 12);
        const rowH = innerH / Math.max(visible.length, 1);
        const laneH = Math.max(2, (rowH - 4) / 3);

        const x = scaleLinear().domain([lapMin, lapMax]).range([0, innerW]);
        const tickW = Math.max(2, innerW / Math.max(lapMax - lapMin + 1, 1) - 0.5);

        return (
          <svg width={width} height={height}>
            <g transform={`translate(${margin.left},${margin.top})`}>
              {visible.map((r, i) => {
                const yBase = i * rowH + 1;
                return (
                  <g key={r.driver}>
                    <text x={-6} y={yBase + rowH * 0.6} textAnchor="end" fontSize="9" fill="#9ca3af">
                      {r.driver}
                    </text>
                    {[0, 1, 2].map((k) => {
                      const yLane = yBase + k * (laneH + 1);
                      return (
                        <g key={k}>
                          <rect x={0} y={yLane} width={innerW} height={laneH} fill="#1f1f23" />
                          {r.sectors[k].map((lap) => (
                            <rect
                              key={lap}
                              x={x(lap) - tickW / 2}
                              y={yLane}
                              width={tickW}
                              height={laneH}
                              fill={SECTOR_COLORS[k]}
                              opacity={0.95}
                            >
                              <title>{`${r.driver} · lap ${lap} · fastest S${k + 1}`}</title>
                            </rect>
                          ))}
                        </g>
                      );
                    })}
                    <text
                      x={innerW + 4}
                      y={yBase + rowH * 0.6}
                      fontSize="9"
                      fill="#e5e5e5"
                    >
                      {r.total}
                    </text>
                  </g>
                );
              })}
            </g>
            <g transform={`translate(${margin.left},${height - 4})`}>
              {['S1', 'S2', 'S3'].map((lbl, i) => (
                <g key={lbl} transform={`translate(${i * 36},-4)`}>
                  <rect width={8} height={8} fill={SECTOR_COLORS[i]} opacity={0.9} />
                  <text x={12} y={7} fontSize="9" fill="#6b7280">{lbl}</text>
                </g>
              ))}
              <text x={innerW - margin.left} y={3} textAnchor="end" fontSize="9" fill="#6b7280">
                purples
              </text>
            </g>
          </svg>
        );
      }}
    </Chart>
  );
};
